import {
  type CarePlanState,
  INITIAL_CARE_PLAN_STATUS_OPTIONS,
} from '@seen/core/models/care-plans/constants'
import type { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { type JSX, useCallback } from 'react'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

interface InitialCarePlanStatusComboboxProps {
  carePlan: InitialCarePlan
}

export function InitialCarePlanStatusCombobox({
  carePlan,
}: InitialCarePlanStatusComboboxProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const showAdminActions = usePortalStore((state) => state.showAdminActions)
  const { mutate: mutateTransaction } = useTransactionMutation()
  const practitionerRef = useCurrentPractitioner().reference
  const { open: confirmWithAnnotation } = useConfirmWithAnnotationStore()

  const handleChangeStatus = useCallback(
    (state: CarePlanState | null) => {
      if (state == null) {
        return
      }

      confirmWithAnnotation({
        title: 'Confirm Care Plan Status Change',
        description: `Are you sure you want to change the care plan status to ${state}?`,
        onConfirm: (note: string) => {
          mutateTransaction(async (transaction) => {
            transaction.patch(carePlan, (cp) => {
              cp.state = state
            })

            const trimmedNote = note?.trim()
            if (trimmedNote) {
              transaction.create(
                new CommentCommunication({
                  partOf: [carePlan.reference],
                  message: `Changed status to ${state}: ${trimmedNote}`,
                  sender: practitionerRef,
                  subject: carePlan.patientSubject,
                })
              )
            }
          })
        },
      })
    },
    [carePlan, confirmWithAnnotation, mutateTransaction, practitionerRef]
  )

  return (
    <ComboboxButton
      options={INITIAL_CARE_PLAN_STATUS_OPTIONS}
      disabled={!showAdminActions || !carePlan.isEditable || isReadOnly}
      variant="status"
      value={carePlan.state}
      onChange={handleChangeStatus}
    />
  )
}
